import { useMemo } from 'react';
import { useStore } from '@nanostores/react';
import * as Sentry from '@sentry/react';
import { accountStore, userStore } from './store';
import type { AugmentedAccount, AugmentedUser } from './types';

type Auth = {
  account: AugmentedAccount;
  user: AugmentedUser;
};

/**
 * Reads the authenticated account/user from the global nanostores that
 * `useAuthQuery` populates. Because these are global stores (not a React
 * context scoped inside the authenticated layout), `useAuth()` works anywhere
 * in the tree — including Pluma managed modals, which render at the root Pluma
 * provider above the authenticated layout.
 *
 * The stores are expected to be populated wherever `useAuth()` is called (the
 * authenticated layout only renders its subtree once auth has resolved). If
 * they aren't:
 * - in dev/test we throw, so the misuse surfaces loudly;
 * - in production we report to Sentry and return the (possibly null) values
 *   rather than throwing, so a transient empty read — e.g. the stores being
 *   cleared on logout/session invalidation while the authenticated shell is
 *   still mounted, just before the hard redirect unloads the page — degrades
 *   gracefully instead of taking down the whole React tree. Callers that may
 *   render before auth resolves should tolerate a null account/user.
 */
export function useAuth(): Auth {
  const account = useStore(accountStore);
  const user = useStore(userStore);

  return useMemo(() => {
    if (account === null || user === null) {
      const error = new Error('useAuth was called before the account/user stores were populated');
      if (import.meta.env.DEV) {
        throw error;
      }
      Sentry.captureException(error);
    }
    return { account, user } as Auth;
  }, [account, user]);
}

/** The signed-in user's numeric id (already coerced to a number by the auth layer). */
export function useCurrentUserId(): number {
  return useAuth().user.id;
}
